import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Paper, 
  Typography,
  Grid,
  Alert,
  TextField,
  MenuItem,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  Autocomplete,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import toast from 'react-hot-toast';
import { appLogger } from '../../utils/errorLogger';
import * as clientApi from '../../api/clients';
import * as propertyApi from '../../api/properties';
import * as utilityApi from '../../api/utilityServices';
import * as lookupApi from '../../api/lookups';
import { LookupType } from '../../types/lookup.types';
import * as supplierApi from '../../api/suppliers';
import * as subServiceApi from '../../api/subServices';
import { Client } from '../../types/client';
import { Property, PropertyAddress } from '../../types/property.types';
import { 
  UtilityService, 
  UtilityStatus, 
  UtilityType, 
  Supplier, 
  SubService, 
  CreateUtilityServiceDTO
} from '../../types/utility.types';
import {
  EnergyForm,
  WaterForm,
  WasteForm,
  TelecomsForm,
  OtherForm,
} from '../../components/ContractForms';
import CommissionPaymentManager from '../../components/CommissionPayments/CommissionPaymentManager';

// Our internal form data structure - different from API types to handle UI state
interface FormData {
  clientId?: string;
  propertyId?: string;
  serviceType?: UtilityType;
  supplier?: string;
  status?: UtilityStatus;
  accountNumber?: string;
  contractStartDate?: Date | null;
  contractEndDate?: Date | null;
  currentRate?: string;
  monthlyCost?: number;
  eacAq?: string;
  meterDetails?: {
    meterType?: string;
    serialNumber?: string;
    location?: string;
    mpanMprnSpid?: string;
  };
  subServices?: string[];
  notes?: string;
  // Updated numeric fields
  standingCharge?: number;
  commissionUplift?: string;
  contractValue?: string;
  annualUsage?: string;
  // Legacy fields (will be deprecated)
  dayUnitRate?: string;
  nightUnitRate?: string;
  eveningWeekendRate?: string;
  eveningWeekendConsumption?: string;
  // New energy rate fields as numeric types
  dayRate?: number;
  dayConsumption?: number;
  nightRate?: number;
  nightConsumption?: number;
  weekendRate?: number;
  weekendConsumption?: number;
  uplift?: number;
}

// Helper function to get status color - dynamic based on common patterns
const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  const normalizedStatus = status?.toUpperCase();
  
  // Success statuses (active/live states)
  if (normalizedStatus.includes('LIVE')) {
    return 'success';
  }
  
  // Warning statuses (in progress/awaiting states)
  if (normalizedStatus.includes('AWAITING') || 
      normalizedStatus.includes('PENDING') || 
      normalizedStatus.includes('SIGNED') ||
      normalizedStatus.includes('COT') || 
      normalizedStatus.includes('COO') ||
      normalizedStatus.includes('LOCKED')) {
    return 'warning';
  }
  
  // Error statuses (negative outcomes)
  if (normalizedStatus.includes('CANCELLED') || 
      normalizedStatus.includes('LOST') || 
      normalizedStatus.includes('TERMINATED')) {
    return 'error';
  }
  
  // Info statuses (special cases)
  if (normalizedStatus.includes('OBJECTION') || 
      normalizedStatus.includes('RENEWED')) {
    return 'info';
  }
  
  // Default for any other status
  return 'default';
};

const ContractForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const clientIdFromQuery = queryParams.get('clientId');
  const propertyIdFromQuery = queryParams.get('propertyId');

  // Get contract ID from URL params
  const { id } = useParams<{ id?: string }>();
  const [contractId, setContractId] = useState<string>(id || '');

  // Determine if we're in edit mode or view mode
  const pathIncludesEdit = location.pathname.includes('/edit');
  const [isEditMode, setIsEditMode] = useState<boolean>(pathIncludesEdit);
  const [isViewMode, setIsViewMode] = useState<boolean>(!!id && !pathIncludesEdit);

  // Update mode when path changes
  useEffect(() => {
    const editMode = location.pathname.includes('/edit');
    const viewMode = !!id && !editMode;
    
    setIsEditMode(editMode);
    setIsViewMode(viewMode);
    
    console.log('Form Mode Updated:', { id, editMode, viewMode, pathname: location.pathname });
  }, [location.pathname, id]);

  const [formData, setFormData] = useState<FormData>({
    serviceType: '' as UtilityType, // No default value
    accountNumber: '',
    status: '' as UtilityStatus, // No default value
    notes: '',
    supplier: '',
    meterDetails: {
      meterType: '',
      location: '',
      serialNumber: '',
      mpanMprnSpid: '',
    },
    subServices: [],
    contractStartDate: null,
    contractEndDate: null,
    currentRate: '',
    eacAq: '',
    clientId: clientIdFromQuery || undefined,
    propertyId: propertyIdFromQuery || undefined,
    standingCharge: undefined,
    commissionUplift: '',
    contractValue: '',
    annualUsage: '',
    // Initialize new numeric fields
    dayRate: undefined,
    dayConsumption: undefined,
    nightRate: undefined,
    nightConsumption: undefined,
    weekendRate: undefined,
    weekendConsumption: undefined,
    uplift: undefined,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [properties, setProperties] = useState<Property[]>([]);
  const [serviceTypes, setServiceTypes] = useState<UtilityType[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [availableSubServices, setAvailableSubServices] = useState<SubService[]>([]);
  const [, setShowContractFields] = useState<boolean>(false);
  const [, setSubmitting] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Load client details
  const loadClient = useCallback(async (id: string) => {
    try {
      const client = await clientApi.getClient(id);
      setSelectedClient(client);
      setFormData(prev => ({
        ...prev,
        clientId: id,
      }));
    } catch (err) {
      console.error('Failed to load client:', err);
      appLogger.error(err instanceof Error ? err : new Error('Failed to load client'), {
        metadata: { clientId: id },
        source: 'ContractForm.loadClient'
      });
      toast.error('Failed to load client details');
    }
  }, []);

  // Handle form field changes
  const handleFormChange = useCallback((field: string, value: any) => {
    console.log(`Updating field: ${field} with value:`, value);
    
    setFormData((prev) => {
      const newFormData = {
        ...prev,
        [field]: value,
      };
      
      // Special handling for serviceType changes
      if (field === 'serviceType') {
        console.log('Service type changed to:', value);
        // Reset service-specific fields when type changes
        newFormData.meterDetails = {
          meterType: '',
          location: '',
          serialNumber: '',
          mpanMprnSpid: '',
        };
        newFormData.eacAq = '';
        newFormData.standingCharge = undefined;
        newFormData.contractValue = '';
        newFormData.subServices = [];
        
        // Reset the legacy energy rate fields
        newFormData.dayUnitRate = '';
        newFormData.nightUnitRate = '';
        newFormData.eveningWeekendRate = '';
        newFormData.eveningWeekendConsumption = '';
        
        // Reset the new numeric energy rate fields
        newFormData.dayRate = undefined;
        newFormData.dayConsumption = undefined;
        newFormData.nightRate = undefined;
        newFormData.nightConsumption = undefined;
        newFormData.weekendRate = undefined;
        newFormData.weekendConsumption = undefined;
        newFormData.uplift = undefined;
      }
      
      return newFormData;
    });
  }, []);

  // Load properties for a client
  const loadProperties = useCallback(async (clientId: string) => {
    if (!clientId) {
      setProperties([]);
      return;
    }
    
    try {
      const fetchedProperties = await propertyApi.getClientProperties(clientId);
      setProperties(fetchedProperties || []);
      
      // If there's only one property, select it automatically
      if (fetchedProperties && fetchedProperties.length === 1 && !propertyIdFromQuery) {
        handleFormChange('propertyId', fetchedProperties[0]._id);
      }
    } catch (err) {
      console.error('Failed to load properties:', err);
      appLogger.error(err instanceof Error ? err : new Error('Failed to load properties'), {
        metadata: { clientId },
        source: 'ContractForm.loadProperties'
      });
      toast.error('Failed to load properties for this client');
    }
  }, [propertyIdFromQuery, handleFormChange]);

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        
        // Load clients
        const fetchedClients = await clientApi.getClients();
        setClients(fetchedClients || []);
        
        // Load suppliers
        const fetchedSuppliers = await supplierApi.fetchSuppliers();
        setSuppliers(fetchedSuppliers || []);
        
        // Load service types and statuses from lookups
        try {
          // Use the correct lookup type as defined in the enum
          const lookups = await lookupApi.getLookupsByType('ServiceType' as LookupType);
          console.log('ServiceType lookups:', lookups);
          
          if (lookups && lookups.length > 0) {
            // Each lookup has its own value property
            const types = lookups.map(lookup => lookup.value as UtilityType);
            console.log('ServiceType values:', types);
            setServiceTypes(types);
          } else {
            // Fallback if lookups not found
            const fallbackTypes = [
              'Gas',
              'Electricity',
              'EV',
              'PPA Export Contract',
              'Renewables',
              'Telecoms',
              'Waste',
              'Water',
            ] as UtilityType[];
            console.log('Using fallback service types:', fallbackTypes);
            setServiceTypes(fallbackTypes);
          }

          // Use the correct lookup type as defined in the enum
          const statusLookups = await lookupApi.getLookupsByType('UtilityServiceStatus' as LookupType);
          console.log('Status lookups:', statusLookups);
          
          if (statusLookups && statusLookups.length > 0) {
            // Each lookup has its own value property
            const statuses = statusLookups.map(lookup => lookup.value as string);
            console.log('Status values:', statuses);
            setStatuses(statuses);
          } else {
            console.warn('No status lookups found in AdminPanel');
            setStatuses([]);
          }
        } catch (err) {
          console.error('Failed to load lookups:', err);
          appLogger.error(err instanceof Error ? err : new Error('Failed to load lookups'), {
            source: 'ContractForm.loadInitialData'
          });
          // Fallback values
          setServiceTypes([
            'Gas',
            'Electricity',
            'EV',
            'PPA Export Contract',
            'Renewables',
            'Telecoms',
            'Waste',
            'Water',
          ] as UtilityType[]);
          setStatuses(['LIVE', 'PENDING', 'TERMINATED']);
        }
      } catch (err) {
        console.error('Failed to load initial data:', err);
        appLogger.error(err instanceof Error ? err : new Error('Failed to load initial data'), {
          source: 'ContractForm.loadInitialData'
        });
        setError('Failed to load initial data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();

    // If editing or viewing an existing contract, load it
    if (contractId) {
      const loadContract = async () => {
        try {
          setLoading(true);
          
          const contract = await utilityApi.getUtilityService(contractId);
          
          if (!contract) {
            setError('Contract not found');
            return;
          }
          
          setContractId(contract._id);
          setFormData({
            serviceType: contract.serviceType || '',
            accountNumber: contract.accountNumber || '',
            status: contract.status || '',
            notes: contract.notes || '',
            supplier: (() => {
              // If it's a string, return it directly
              if (typeof contract.supplier === 'string') return contract.supplier;
              // If it's an object with an _id property, return the _id
              if (contract.supplier && typeof contract.supplier === 'object') {
                return '_id' in contract.supplier ? (contract.supplier as any)._id : '';
              }
              // Default fallback
              return '';
            })(),
            meterDetails: {
              meterType: contract.meterDetails?.meterType || '',
              location: contract.meterDetails?.location || '',
              serialNumber: contract.meterDetails?.serialNumber || '',
              mpanMprnSpid: contract.meterDetails?.mpanMprnSpid || ''
            },
            subServices: Array.isArray(contract.subServices) ? contract.subServices.map(s => {
              // If it's a string, return it directly
              if (typeof s === 'string') return s;
              // If it's an object with an _id property, return the _id
              if (s && typeof s === 'object') {
                return '_id' in s ? (s as any)._id : '';
              }
              // Default fallback
              return '';
            }) : [],
            contractStartDate: contract.contractStartDate ? new Date(contract.contractStartDate) : null,
            contractEndDate: contract.contractEndDate ? new Date(contract.contractEndDate) : null,
            currentRate: contract.currentRate || '',
            eacAq: contract.eacAq || '',
            clientId: clientIdFromQuery || (typeof contract.client === 'string' ? contract.client : (contract.client && typeof contract.client === 'object' && '_id' in contract.client ? contract.client._id : '')),
            propertyId: propertyIdFromQuery || (typeof contract.property === 'string' ? contract.property : (contract.property && typeof contract.property === 'object' && '_id' in contract.property ? contract.property._id : '')),
            standingCharge: typeof contract.standingCharge === 'number' ? contract.standingCharge : undefined,
            commissionUplift: contract.commissionUplift || '',
            contractValue: contract.contractValue || '',
            annualUsage: contract.annualUsage || '',
            // Load legacy fields
            dayUnitRate: contract.dayUnitRate || '',
            nightUnitRate: contract.nightUnitRate || '',
            eveningWeekendRate: contract.eveningWeekendRate || '',
            eveningWeekendConsumption: contract.eveningWeekendConsumption || '',
            // Load new energy rate fields as numeric types
            dayRate: typeof contract.dayRate === 'number' ? contract.dayRate : undefined,
            dayConsumption: typeof contract.dayConsumption === 'number' ? contract.dayConsumption : undefined,
            nightRate: typeof contract.nightRate === 'number' ? contract.nightRate : undefined,
            nightConsumption: typeof contract.nightConsumption === 'number' ? contract.nightConsumption : undefined,
            weekendRate: typeof contract.weekendRate === 'number' ? contract.weekendRate : undefined,
            weekendConsumption: typeof contract.weekendConsumption === 'number' ? contract.weekendConsumption : undefined,
            uplift: typeof contract.uplift === 'number' ? contract.uplift : undefined,
          });
          
          // Load client and properties from contract data
          const contractClientId = typeof contract.client === 'string' ? contract.client : (contract.client && typeof contract.client === 'object' && '_id' in contract.client ? contract.client._id : '');
          
          if (contractClientId) {
            try {
              await loadClient(contractClientId);
              await loadProperties(contractClientId);
            } catch (clientErr) {
              console.error('Failed to load client/property data:', clientErr);
              appLogger.error(clientErr instanceof Error ? clientErr : new Error('Failed to load client/property data'), {
                metadata: { contractId: id, clientId: contractClientId },
                source: 'ContractForm.loadContract'
              });
              // Don't throw - allow viewing contract even if client data is missing
            }
          } else if (clientIdFromQuery) {
            try {
              await loadClient(clientIdFromQuery);
              if (propertyIdFromQuery) {
                await loadProperties(clientIdFromQuery);
              }
            } catch (clientErr) {
              console.error('Failed to load client/property data from query:', clientErr);
              appLogger.error(clientErr instanceof Error ? clientErr : new Error('Failed to load client/property data from query'), {
                metadata: { contractId: id, clientId: clientIdFromQuery, propertyId: propertyIdFromQuery },
                source: 'ContractForm.loadContract'
              });
            }
          } else {
            console.warn('Contract has no associated client - data may need cleansing');
            appLogger.warning('Contract has no associated client', {
              metadata: { contractId: id },
              source: 'ContractForm.loadContract'
            });
          }
          
          // Always show contract fields in view mode
          setShowContractFields(true);
        } catch (err: any) {
          console.error('Failed to load contract:', err);
          appLogger.error(err instanceof Error ? err : new Error('Failed to load contract'), {
            metadata: { contractId },
            statusCode: err.response?.status,
            source: 'ContractForm.loadContract'
          });
          
          // Check if it's a 404 error
          if (err.response && err.response.status === 404) {
            setError(`Contract with ID ${contractId} not found. The contract may have been deleted or you may not have permission to view it.`);
          } else {
            setError(`Failed to load contract: ${err.message || 'Unknown error'}`);
          }
        } finally {
          setLoading(false);
        }
      };
      
      loadContract();
    } else if (clientIdFromQuery) {
      // If no contract ID but client ID is provided
      const loadClientData = async () => {
        try {
          setLoading(true);
          await loadClient(clientIdFromQuery);
          
          if (propertyIdFromQuery) {
            await loadProperties(clientIdFromQuery);
            setFormData(prev => ({
              ...prev,
              propertyId: propertyIdFromQuery
            }));
          } else {
            await loadProperties(clientIdFromQuery);
          }
        } catch (err) {
          console.error('Failed to load client data:', err);
          appLogger.error(err instanceof Error ? err : new Error('Failed to load client data'), {
            metadata: { clientId: clientIdFromQuery },
            source: 'ContractForm.loadClientData'
          });
          setError('Failed to load client data. Please try again.');
        } finally {
          setLoading(false);
        }
        
        // If both client and property are provided, show contract fields
        if (clientIdFromQuery && propertyIdFromQuery) {
          setShowContractFields(true);
          loadProperties(clientIdFromQuery);
          setFormData(prev => ({
            ...prev,
            clientId: clientIdFromQuery,
          }));
        }
      };
      
      loadClientData();
    } else {
      // New contract without client/property in query
      setShowContractFields(false);
    }
  }, [contractId, clientIdFromQuery, propertyIdFromQuery, id, loadProperties, loadClient]);

  // Load contract data - reusable function
  const loadContract = async () => {
    if (!contractId) return;
    
    try {
      setLoading(true);
      
      const contract = await utilityApi.getUtilityService(contractId);
      
      if (!contract) {
        setError('Contract not found');
        return;
      }
      
      setContractId(contract._id);
      setFormData({
        serviceType: contract.serviceType || '',
        accountNumber: contract.accountNumber || '',
        status: contract.status || '',
        notes: contract.notes || '',
        supplier: (() => {
          if (typeof contract.supplier === 'string') return contract.supplier;
          if (contract.supplier && typeof contract.supplier === 'object') {
            return '_id' in contract.supplier ? (contract.supplier as any)._id : '';
          }
          return '';
        })(),
        meterDetails: {
          meterType: contract.meterDetails?.meterType || '',
          location: contract.meterDetails?.location || '',
          serialNumber: contract.meterDetails?.serialNumber || '',
          mpanMprnSpid: contract.meterDetails?.mpanMprnSpid || ''
        },
        subServices: Array.isArray(contract.subServices) ? contract.subServices.map(s => {
          if (typeof s === 'string') return s;
          if (s && typeof s === 'object') {
            return '_id' in s ? (s as any)._id : '';
          }
          return '';
        }) : [],
        contractStartDate: contract.contractStartDate ? new Date(contract.contractStartDate) : null,
        contractEndDate: contract.contractEndDate ? new Date(contract.contractEndDate) : null,
        currentRate: contract.currentRate || '',
        eacAq: contract.eacAq || '',
        clientId: clientIdFromQuery || (typeof contract.client === 'string' ? contract.client : (contract.client && typeof contract.client === 'object' && '_id' in contract.client ? contract.client._id : '')),
        propertyId: propertyIdFromQuery || (typeof contract.property === 'string' ? contract.property : (contract.property && typeof contract.property === 'object' && '_id' in contract.property ? contract.property._id : '')),
        standingCharge: typeof contract.standingCharge === 'number' ? contract.standingCharge : undefined,
        commissionUplift: contract.commissionUplift || '',
        contractValue: contract.contractValue || '',
        annualUsage: contract.annualUsage || '',
        dayUnitRate: contract.dayUnitRate || '',
        nightUnitRate: contract.nightUnitRate || '',
        eveningWeekendRate: contract.eveningWeekendRate || '',
        eveningWeekendConsumption: contract.eveningWeekendConsumption || '',
        dayRate: typeof contract.dayRate === 'number' ? contract.dayRate : undefined,
        dayConsumption: typeof contract.dayConsumption === 'number' ? contract.dayConsumption : undefined,
        nightRate: typeof contract.nightRate === 'number' ? contract.nightRate : undefined,
        nightConsumption: typeof contract.nightConsumption === 'number' ? contract.nightConsumption : undefined,
        weekendRate: typeof contract.weekendRate === 'number' ? contract.weekendRate : undefined,
        weekendConsumption: typeof contract.weekendConsumption === 'number' ? contract.weekendConsumption : undefined,
        uplift: typeof contract.uplift === 'number' ? contract.uplift : undefined,
      });
      
      const contractClientId = typeof contract.client === 'string' ? contract.client : (contract.client && typeof contract.client === 'object' && '_id' in contract.client ? contract.client._id : '');
      
      if (contractClientId) {
        try {
          await loadClient(contractClientId);
          await loadProperties(contractClientId);
        } catch (clientErr) {
          console.error('Failed to load client/property data:', clientErr);
        }
      }
      
      setShowContractFields(true);
    } catch (err: any) {
      console.error('Failed to load contract:', err);
      if (err.response && err.response.status === 404) {
        setError(`Contract with ID ${contractId} not found.`);
      } else {
        setError(`Failed to load contract: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Load sub-services when service type changes
  useEffect(() => {
    if (formData.serviceType) {
      const loadSubServices = async () => {
        try {
          console.log('Loading sub-services for:', formData.serviceType);
          const fetchedSubServices = await subServiceApi.fetchSubServices(formData.serviceType as UtilityType);
          console.log('Fetched sub-services:', fetchedSubServices);
          setAvailableSubServices(fetchedSubServices);
        } catch (err) {
          console.error('Failed to load sub-services:', err);
          appLogger.error(err instanceof Error ? err : new Error('Failed to load sub-services'), {
            metadata: { serviceType: formData.serviceType },
            source: 'ContractForm.loadSubServices'
          });
          setAvailableSubServices([]);
        }
      };
      
      loadSubServices();
    } else {
      // Clear sub-services when no service type is selected
      setAvailableSubServices([]);
    }
  }, [formData.serviceType]);

  // Handle nested field changes (like meterDetails)
  const handleNestedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    setFormData((prev) => {
      if (name.includes('.')) {
        const [parent, child] = name.split('.');
        const parentObj = prev[parent as keyof FormData];
        
        if (parentObj && typeof parentObj === 'object' && !Array.isArray(parentObj)) {
          return {
            ...prev,
            [parent]: {
              ...parentObj,
              [child]: value,
            },
          };
        }
      }
      
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  // Handle sub-service selection
  const handleSubServiceChange = (subServiceId: string) => {
    setFormData(prev => ({
      ...prev,
      subServices: prev.subServices?.includes(subServiceId)
        ? prev.subServices.filter(id => id !== subServiceId)
        : [...(prev.subServices || []), subServiceId],
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // If in view mode, switch to edit mode
    if (isViewMode) {
      console.log('Switching to edit mode');
      navigate(`/utilities/contracts/${contractId}/edit${location.search}`);
      return;
    }
    
    setSubmitting(true);
    setValidationErrors([]);
    
    try {
      // Validate required fields
      const errors: string[] = [];
      
      if (!formData.clientId && !clientIdFromQuery) {
        errors.push('Client is required');
      }
      
      if (!formData.propertyId && !propertyIdFromQuery) {
        errors.push('Property is required');
      }
      
      if (!formData.serviceType) {
        errors.push('Service type is required');
      }
      
      if (!formData.supplier) {
        errors.push('Supplier is required');
      }
      
      if (!formData.status) {
        errors.push('Status is required');
      }
      
      // Validate meter details for specific service types
      if (formData.serviceType && ['Electricity', 'Gas'].includes(formData.serviceType) && !formData.meterDetails?.mpanMprnSpid) {
        errors.push('Meter identifier (MPAN/MPRN) is required');
      }
      
      // Validate SPID for Water contracts (stored in meterType field)
      if (formData.serviceType === 'Water' && !formData.meterDetails?.meterType) {
        errors.push('SPID is required for water contracts');
      }
      
      // Validate energy-specific fields for gas and electricity
      if (formData.serviceType && ['Electricity', 'Gas'].includes(formData.serviceType)) {
        if (formData.dayRate === undefined || formData.dayRate === null) {
          errors.push('Day Unit Rate (p/kWh) is required for gas and electricity contracts');
        }
        
        if (formData.dayConsumption === undefined || formData.dayConsumption === null) {
          errors.push('Day Consumption (kWh) is required for gas and electricity contracts');
        }
        
        if (formData.standingCharge === undefined || formData.standingCharge === null) {
          errors.push('Standing Charge is required for gas and electricity contracts');
        }
      }
      
      // Validate contract end date is not in the past
      if (formData.contractEndDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day for fair comparison
        const endDate = new Date(formData.contractEndDate);
        endDate.setHours(0, 0, 0, 0);
        
        if (endDate < today) {
          errors.push('Contract end date cannot be in the past');
        }
      }
      
      if (errors.length > 0) {
        // Display all validation errors
        const errorMessage = errors.length === 1 
          ? errors[0]
          : `Please fix the following ${errors.length} errors:\n${errors.map(e => `• ${e}`).join('\n')}`;
        
        setValidationErrors(errors);
        toast.error(errorMessage, { duration: 6000 });
        setSubmitting(false);
        
        // Scroll to top to show errors
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      
      // Normalize service type to match server-side enum values
      let normalizedServiceType = formData.serviceType;
      
      // Map common variations to standard types accepted by the server
      if (formData.serviceType && formData.serviceType.toUpperCase().includes('ELECTRIC')) normalizedServiceType = 'Electricity';
      if (formData.serviceType && formData.serviceType.toUpperCase().includes('Gas')) normalizedServiceType = 'Gas';
      if (formData.serviceType && formData.serviceType.toUpperCase().includes('Water')) normalizedServiceType = 'Water';
      if (formData.serviceType && formData.serviceType.toUpperCase().includes('TELECOM')) normalizedServiceType = 'Telecoms';
      if (formData.serviceType && formData.serviceType.toUpperCase().includes('Waste')) normalizedServiceType = 'Waste';
      if (formData.serviceType && formData.serviceType.toUpperCase().includes('NEW CONNECTION')) normalizedServiceType = 'New Connection';
      
      console.log(`Normalizing service type from ${formData.serviceType} to ${normalizedServiceType}`);
      
      // Create a new object without spreading formData to avoid type issues
      const contractData: CreateUtilityServiceDTO = {
        clientId: formData.clientId,
        propertyId: formData.propertyId,
        serviceType: normalizedServiceType ? (normalizedServiceType as UtilityType) : 'Gas' as UtilityType,
        supplier: formData.supplier || '', // Ensure supplier is always a string
        status: formData.status ? (formData.status as UtilityStatus) : 'PENDING' as UtilityStatus,
        accountNumber: formData.accountNumber,
        contractStartDate: formData.contractStartDate ? formData.contractStartDate.toISOString() : undefined,
        contractEndDate: formData.contractEndDate ? formData.contractEndDate.toISOString() : undefined,
        currentRate: formData.currentRate,
        eacAq: formData.eacAq,
        meterDetails: {
          meterType: formData.meterDetails?.meterType || '',
          serialNumber: formData.meterDetails?.serialNumber || '',
          location: formData.meterDetails?.location || '',
          mpanMprnSpid: formData.meterDetails?.mpanMprnSpid || ''
        },
        subServices: formData.subServices || [],
        notes: formData.notes,
        // Include additional fields that weren't being persisted
        standingCharge: formData.standingCharge,
        commissionUplift: formData.commissionUplift,
        contractValue: formData.contractValue,
        annualUsage: formData.annualUsage,
        // Include new energy rate fields
        dayRate: formData.dayRate,
        dayConsumption: formData.dayConsumption,
        nightRate: formData.nightRate,
        nightConsumption: formData.nightConsumption,
        weekendRate: formData.weekendRate,
        weekendConsumption: formData.weekendConsumption,
        uplift: formData.uplift,
        // Include legacy fields for backward compatibility
        dayUnitRate: formData.dayUnitRate,
        nightUnitRate: formData.nightUnitRate,
        eveningWeekendRate: formData.eveningWeekendRate,
        eveningWeekendConsumption: formData.eveningWeekendConsumption
      };
      
      console.log('Submitting contract data:', contractData);

      // Create or update contract
      if (contractId) {
        // Update existing contract
        await utilityApi.updateUtilityService(
          contractId,
          contractData,
        );
        toast.success(
          `Contract for ${formData.serviceType || 'service'} updated successfully!`,
          {
            duration: 4000,
            position: 'top-right',
            icon: '✅',
          }
        );
      } else {
        // Create new contract
        await utilityApi.createUtilityService(
          formData.clientId!,
          formData.propertyId!,
          contractData,
        );
        toast.success(
          `Contract for ${formData.serviceType || 'service'} created successfully!`,
          {
            duration: 4000,
            position: 'top-right',
            icon: '✅',
          }
        );
      }
      
      // Navigate back to contracts list
      navigate('/utilities/contracts');
    } catch (err: any) {
      console.error('Failed to save contract:', err);
      appLogger.error(err instanceof Error ? err : new Error('Failed to save contract'), {
        metadata: { contractId: id, formData },
        source: 'ContractForm.handleSubmit'
      });
      
      // Build user-friendly error message
      let errorMessage = `Failed to ${contractId ? 'update' : 'create'} contract. `;
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.errors) {
        const fieldErrors = err.response.data.errors;
        const errorList = Object.entries(fieldErrors)
          .map(([field, msg]) => `• ${field}: ${msg}`)
          .join('\n');
        errorMessage = `Please fix the following errors:\n${errorList}`;
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid contract data. Please check all required fields and try again.';
      } else if (err.response?.status === 409) {
        errorMessage = 'A contract with this information already exists.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error occurred. Please try again or contact support if the problem persists.';
      } else if (err.message) {
        errorMessage = err.message;
      } else {
        errorMessage = 'An unexpected error occurred. Please try again.';
      }
      
      toast.error(errorMessage, { duration: 6000 });
      
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  // Format address for display
  const formatAddress = (address: PropertyAddress) => {
    if (!address) return 'N/A';
    
    const parts = [
      address.line1,
      address.line2,
      address.city,
      address.county,
      address.postcode,
    ].filter(Boolean);
    
    return parts.join(', ');
  };
  
  // Format service type in proper case for display
  const formatServiceType = (type: UtilityType | string): string => {
    if (!type) return '';
    
    // Handle special cases
    if (type === 'PPA Export Contract') return 'PPA Export Contract';
    if (type === 'EV') return 'EV';
    
    // Convert other types to proper case
    return type.toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Render the appropriate form based on the service type
  const renderServiceTypeForm = () => {
    // Cast formData to UtilityService for the component props
    const utilityFormData = formData as unknown as UtilityService;
    
    const commonProps = {
      formData: utilityFormData,
      handleFormChange,
      handleNestedChange,
      isViewMode,
    };

    console.log('Rendering form for service type:', formData.serviceType);
    
    // Normalize the service type for case-insensitive comparison
    const serviceType = formData.serviceType?.toUpperCase();
    console.log('Normalized service type for form selection:', serviceType);
    
    // Check if the service type contains these keywords rather than exact match
    const isGasOrElectricity = serviceType?.includes('GAS') || serviceType?.includes('ELECTRIC');
    const isWater = serviceType?.includes('WATER');
    const isWaste = serviceType?.includes('WASTE');
    const isTelecoms = serviceType?.includes('TELECOM');
    
    if (isGasOrElectricity) {
      console.log('Rendering EnergyForm');
      return <EnergyForm {...commonProps} />;
    } else if (isWater) {
      console.log('Rendering WaterForm');
      return <WaterForm {...commonProps} />;
    } else if (isWaste) {
      console.log('Rendering WasteForm');
      return <WasteForm {...commonProps} />;
    } else if (isTelecoms) {
      console.log('Rendering TelecomsForm');
      return <TelecomsForm {...commonProps} />;
    } else {
      console.log('Rendering OtherForm as fallback');
      return <OtherForm {...commonProps} />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3,
          backgroundColor: 'rgba(25, 25, 25, 0.8)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 2,
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">
            {isViewMode
              ? 'View Contract'
              : isEditMode
              ? 'Edit Contract'
              : 'New Contract'}
          </Typography>
          
          {isViewMode && (
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => navigate(`/utilities/contracts/${contractId}/edit?clientId=${formData.clientId}&propertyId=${formData.propertyId}`)}
              sx={{
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                backdropFilter: 'blur(4px)',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              }}
            >
              Edit Contract
            </Button>
          )}
        </Box>
        
        {/* Display Contract Type and Status prominently in view mode */}
        {isViewMode && formData.serviceType && formData.status && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Chip 
              label={`Type: ${formData.serviceType.replace(/_/g, ' ')}`} 
              color="primary" 
              variant="outlined" 
              size="medium"
              sx={{ fontSize: '1rem', height: 'auto', padding: '8px 4px' }}
            />
            <Chip 
              label={`Status: ${formData.status}`} 
              color={getStatusColor(formData.status || '')}
              variant="outlined"
              size="medium"
              sx={{ fontSize: '1rem', height: 'auto', padding: '8px 4px' }}
            />
            {formData.accountNumber && (
              <Chip 
                label={`Account: ${formData.accountNumber}`} 
                color="default" 
                variant="outlined" 
                size="medium"
                sx={{ fontSize: '1rem', height: 'auto', padding: '8px 4px' }}
              />
            )}
          </Box>
        )}

        {validationErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="subtitle2">Please fix the following errors:</Typography>
            <ul>
              {validationErrors.map((err, index) => (
                <li key={index}>{err}</li>
              ))}
            </ul>
          </Alert>
        )}

        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : isViewMode ? (
          /* View Mode - Card-based Layout */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Client and Property Information */}
            <Paper 
              elevation={1} 
              sx={{ 
                p: 2, 
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: 'white'
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.2)', pb: 1, color: 'white' }}>
                Client & Property Information
              </Typography>
              {!selectedClient && !formData.clientId && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This contract has no associated client. Data cleansing may be required.
                </Alert>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Client</Typography>
                  <Typography variant="body1" sx={{ color: !selectedClient ? 'warning.main' : 'inherit' }}>
                    {selectedClient?.companyName || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Property</Typography>
                  <Typography variant="body1" sx={{ color: !properties.find(p => p._id === formData.propertyId) ? 'warning.main' : 'inherit' }}>
                    {properties.find(p => p._id === formData.propertyId)?.address?.line1 || 'N/A'}
                    {properties.find(p => p._id === formData.propertyId)?.address?.line2 ? 
                      `, ${properties.find(p => p._id === formData.propertyId)?.address?.line2}` : ''}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
            
            {/* Contract Details */}
            <Paper 
              elevation={1} 
              sx={{ 
                p: 2, 
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: 'white'
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.2)', pb: 1, color: 'white' }}>
                Contract Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Supplier</Typography>
                  <Typography variant="body1">
                    {suppliers.find(s => s._id === formData.supplier)?.name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Account Number</Typography>
                  <Typography variant="body1">{formData.accountNumber || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Contract Start Date</Typography>
                  <Typography variant="body1">
                    {formData.contractStartDate ? new Date(formData.contractStartDate).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Contract End Date</Typography>
                  <Typography variant="body1">
                    {formData.contractEndDate ? new Date(formData.contractEndDate).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Current Rate</Typography>
                  <Typography variant="body1">{formData.currentRate || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Monthly Cost</Typography>
                  <Typography variant="body1">
                    {formData.monthlyCost ? `£${formData.monthlyCost.toFixed(2)}` : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
            
            {/* Service-Specific Details */}
            <Paper 
              elevation={1} 
              sx={{ 
                p: 2, 
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: 'white'
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.2)', pb: 1, color: 'white' }}>
                Service-Specific Details
              </Typography>
              <Box sx={{ p: 1 }}>
                {renderServiceTypeForm()}
              </Box>
            </Paper>
            
            {/* Commission Payments Section - Only show for existing contracts */}
            {contractId && formData.contractValue && (
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 2, 
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Commission Tracking
                </Typography>
                <CommissionPaymentManager 
                  contractId={contractId}
                  contractValue={formData.contractValue ? parseFloat(formData.contractValue.toString()) : 0}
                  onUpdate={loadContract}
                />
              </Paper>
            )}
            
            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/utilities/contracts')}
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)'
                  }
                }}
              >
                Back to Contracts
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(`/utilities/contracts/${contractId}/edit?clientId=${formData.clientId}&propertyId=${formData.propertyId}`)}
                sx={{
                  backgroundColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  backdropFilter: 'blur(4px)',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                }}
              >
                Edit Contract
              </Button>
            </Box>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Client Information Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Client Information</Typography>
                <Grid container spacing={3}>
                  {/* Client Selection */}
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      fullWidth
                      options={[...clients].sort((a, b) => a.companyName.localeCompare(b.companyName))}
                      getOptionLabel={(option) => option.companyName}
                      value={clients.find(c => c._id === formData.clientId) || null}
                      onChange={(_, newValue) => handleFormChange('clientId', newValue?._id || '')}
                      disabled={Boolean(clientIdFromQuery) || Boolean(isViewMode)}
                      slotProps={{
                        paper: {
                          sx: {
                            backgroundColor: 'rgba(30, 30, 30, 0.95)',
                            backdropFilter: 'blur(8px)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            '& .MuiAutocomplete-listbox': {
                              '& .MuiAutocomplete-option': {
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                },
                                '&[aria-selected="true"]': {
                                  backgroundColor: 'rgba(25, 118, 210, 0.2)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.3)',
                                  },
                                },
                              },
                            },
                          },
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Client"
                          required
                          placeholder="Search clients..."
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option._id}>
                          <Box>
                            <Typography variant="body1">{option.companyName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.ownerName} • {option.email}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      isOptionEqualToValue={(option, value) => option._id === value._id}
                    />
                  </Grid>
                  
                  {/* Property Selection */}
                  {(formData.clientId || clientIdFromQuery) && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label="Property"
                        value={formData.propertyId || ''}
                        onChange={(e) => handleFormChange('propertyId', e.target.value)}
                        disabled={Boolean(propertyIdFromQuery) || Boolean(isViewMode)}
                        required
                      >
                        {propertyIdFromQuery && properties.length > 0 ? (
                          <MenuItem value={propertyIdFromQuery}>
                            {properties.find(p => p._id === propertyIdFromQuery)?.name || 'Loading property...'}
                          </MenuItem>
                        ) : (
                          properties.map((property) => (
                            <MenuItem key={property._id} value={property._id}>
                              {property.name} - {formatAddress(property.address)}
                            </MenuItem>
                          ))
                        )}
                      </TextField>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              {/* Contract Details Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Contract Details</Typography>
                <Grid container spacing={3}>
                  {/* Contract Type */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Contract Type"
                      name="serviceType"
                      value={formData.serviceType || ''}
                      onChange={(e) => handleFormChange('serviceType', e.target.value)}
                      disabled={Boolean(isViewMode)}
                      required
                    >
                      <MenuItem value="">Select Contract Type</MenuItem>
                      {serviceTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {formatServiceType(type)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  
                  {/* Status */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Status"
                      name="status"
                      value={formData.status || ''}
                      onChange={(e) => handleFormChange('status', e.target.value)}
                      disabled={Boolean(isViewMode)}
                      required
                    >
                      <MenuItem value="">Select a status</MenuItem>
                      {statuses.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  
                  {/* Supplier */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Supplier"
                      name="supplier"
                      value={formData.supplier || ''}
                      onChange={(e) => handleFormChange('supplier', e.target.value)}
                      disabled={Boolean(isViewMode)}
                      required
                    >
                      <MenuItem value="">Select a supplier</MenuItem>
                      {suppliers.map((supplier) => (
                        <MenuItem key={supplier._id} value={supplier._id}>
                          {supplier.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  
                  {/* Account Number */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Account Number"
                      name="accountNumber"
                      value={formData.accountNumber || ''}
                      onChange={(e) => handleFormChange('accountNumber', e.target.value)}
                      disabled={Boolean(isViewMode)}
                    />
                  </Grid>
                  
                  {/* Contract Start Date */}
                  <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Contract Start Date"
                        value={formData.contractStartDate ? new Date(formData.contractStartDate as any) : null}
                        onChange={(date) => handleFormChange('contractStartDate', date)}
                        disabled={Boolean(isViewMode)}
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: {
                              '& .MuiInputBase-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                color: 'white',
                                '& fieldset': {
                                  borderColor: 'rgba(255, 255, 255, 0.3)'
                                },
                                '&:hover fieldset': {
                                  borderColor: 'rgba(255, 255, 255, 0.5)'
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: 'primary.main'
                                }
                              },
                              '& .MuiInputLabel-root': {
                                color: 'rgba(255, 255, 255, 0.7)',
                                '&.Mui-focused': {
                                  color: 'primary.main'
                                }
                              }
                            }
                          },
                          popper: {
                            sx: {
                              '& .MuiPaper-root': {
                                backgroundColor: 'rgba(25, 25, 25, 0.95)',
                                backdropFilter: 'blur(12px)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                color: 'white',
                                '& .MuiPickersDay-root': {
                                  color: 'white',
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: 'primary.main'
                                  }
                                },
                                '& .MuiPickersCalendarHeader-root': {
                                  color: 'white'
                                },
                                '& .MuiPickersArrowSwitcher-button': {
                                  color: 'white'
                                },
                                '& .MuiDayCalendar-weekDayLabel': {
                                  color: 'rgba(255, 255, 255, 0.7)'
                                }
                              }
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  
                  {/* Contract End Date */}
                  <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Contract End Date"
                        value={formData.contractEndDate ? new Date(formData.contractEndDate as any) : null}
                        onChange={(date) => handleFormChange('contractEndDate', date)}
                        disabled={Boolean(isViewMode)}
                        format="dd/MM/yyyy"
                        minDate={new Date()}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: {
                              '& .MuiInputBase-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                color: 'white',
                                '& fieldset': {
                                  borderColor: 'rgba(255, 255, 255, 0.3)'
                                },
                                '&:hover fieldset': {
                                  borderColor: 'rgba(255, 255, 255, 0.5)'
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: 'primary.main'
                                }
                              },
                              '& .MuiInputLabel-root': {
                                color: 'rgba(255, 255, 255, 0.7)',
                                '&.Mui-focused': {
                                  color: 'primary.main'
                                }
                              }
                            }
                          },
                          popper: {
                            sx: {
                              '& .MuiPaper-root': {
                                backgroundColor: 'rgba(25, 25, 25, 0.95)',
                                backdropFilter: 'blur(12px)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                color: 'white',
                                '& .MuiPickersDay-root': {
                                  color: 'white',
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: 'primary.main'
                                  }
                                },
                                '& .MuiPickersCalendarHeader-root': {
                                  color: 'white'
                                },
                                '& .MuiPickersArrowSwitcher-button': {
                                  color: 'white'
                                },
                                '& .MuiDayCalendar-weekDayLabel': {
                                  color: 'rgba(255, 255, 255, 0.7)'
                                }
                              }
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              </Grid>
              
              {/* Service-Specific Details Section */}
              {formData.serviceType && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    {formatServiceType(formData.serviceType)} Details
                  </Typography>
                  <Box sx={{ border: '1px solid rgba(255, 255, 255, 0.2)', padding: 2, borderRadius: 1, marginBottom: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
                    {renderServiceTypeForm()}
                  </Box>
                </Grid>
              )}
              
              {/* Sub-services Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Additional Services</Typography>
                <FormControl component="fieldset">
                  <FormGroup>
                    {loading ? (
                      <div>Loading sub-services...</div>
                    ) : (
                      <>
                        {availableSubServices.length === 0 ? (
                          <div>No sub-services available for this contract type</div>
                        ) : (
                          availableSubServices.map((subService) => (
                            <FormControlLabel
                              key={subService._id}
                              control={
                                <Checkbox
                                  checked={(formData.subServices || []).includes(subService._id)}
                                  onChange={() => handleSubServiceChange(subService._id)}
                                  disabled={Boolean(isViewMode)}
                                />
                              }
                              label={subService.name}
                            />
                          ))
                        )}
                      </>
                    )}
                  </FormGroup>
                </FormControl>
              </Grid>
              
              {/* Notes Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Notes</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Additional Notes"
                  name="notes"
                  value={formData.notes || ''}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  disabled={Boolean(isViewMode)}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={isViewMode || loading}
                    sx={{ 
                      mr: 2,
                      backgroundColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                      backdropFilter: 'blur(4px)',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    {isEditMode ? 'Update' : 'Create'} Contract
                    {loading && <CircularProgress size={24} sx={{ ml: 1 }} />}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/utilities/contracts')}
                    sx={{
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        backgroundColor: 'rgba(255, 255, 255, 0.08)'
                      }
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        )}
      </Paper>
    </Box>
  );
};

export default ContractForm;
